import React from 'react';
import p5 from 'p5';
import BaseSimulation from './BaseSimulation';

/**
 * Debt Restructuring Simulation - TitanForge Manufacturing
 * 
 * Case Study Context:
 * - 75-year-old manufacturer, $420M revenue, $85M debt
 * - Credit rating improved from BB+ to BBB+
 * - Before: 5.65% weighted avg rate, $4.8M annual interest
 * - After: 4.7% weighted avg rate, $3.65M annual interest
 * - Savings: $1.15M annually, $8.7M NPV
 * 
 * Visualization:
 * - Shows 4 debt facilities with different rates and maturities
 * - Interactive refinancing decisions (click to refinance)
 * - Interest rate comparison (before vs after)
 * - Maturity profile timeline
 * - DSCR (Debt Service Coverage Ratio) improvement
 * - NPV calculation of refinancing benefits
 */

const DebtSimulation: React.FC = () => {
  const sketch = (p: p5) => {
    interface DebtFacility {
      name: string;
      principal: number;
      rate: number;
      originalRate: number;
      newRate: number;
      maturity: number; // years
      refinanced: boolean;
      refinancingCost: number;
      color: p5.Color;
    }
    
    let facilities: DebtFacility[] = [];
    let time = 0;
    let paused = false;
    let speed = 1;
    let selectedFacility = 0;
    let totalInterest = 0;
    let totalSavings = 0;
    
    const ebitda = 34440000; // $34.44M (8.2% of $420M revenue)
    
    p.setup = () => {
      p.createCanvas(800, 500);
      p.frameRate(30);
      
      // Initialize debt facilities from case study
      facilities = [
        {
          name: 'Term Loan A',
          principal: 35000000,
          rate: 6.5,
          originalRate: 6.5,
          newRate: 4.5,
          maturity: 5,
          refinanced: false,
          refinancingCost: 175000,
          color: p.color(255, 100, 100)
        },
        {
          name: 'Senior Bond',
          principal: 20000000,
          rate: 5.5,
          originalRate: 5.5,
          newRate: 4.8,
          maturity: 7,
          refinanced: false,
          refinancingCost: 120000,
          color: p.color(255, 150, 100)
        },
        {
          name: 'Revolver',
          principal: 15000000,
          rate: 5.2,
          originalRate: 5.2,
          newRate: 4.2,
          maturity: 3,
          refinanced: false,
          refinancingCost: 75000,
          color: p.color(100, 150, 255)
        },
        {
          name: 'Equipment Loan',
          principal: 15000000,
          rate: 5.8,
          originalRate: 5.8,
          newRate: 4.9,
          maturity: 6,
          refinanced: false,
          refinancingCost: 80000,
          color: p.color(150, 100, 255)
        }
      ];
      
      calculateMetrics();
    };
    
    p.mousePressed = () => {
      if (p.mouseX > 0 && p.mouseX < p.width && p.mouseY > 0 && p.mouseY < p.height) {
        // Check if clicking on facility bars
        const barY = 100;
        const barH = 40;
        const spacing = 50;
        
        for (let i = 0; i < facilities.length; i++) {
          const y = barY + i * spacing;
          if (p.mouseY > y && p.mouseY < y + barH && p.mouseX > 50 && p.mouseX < 400) {
            selectedFacility = i;
            return false;
          }
          
          // Check refinance button
          if (p.mouseY > y + 5 && p.mouseY < y + 35 && p.mouseX > 410 && p.mouseX < 510) {
            toggleRefinance(i);
            return false;
          }
        }
        
        paused = !paused;
        return false;
      }
    };
    
    p.keyPressed = () => {
      if (p.key === ' ') paused = !paused;
      if (p.key === 'r' || p.key === 'R') {
        // Reset all refinancing
        facilities.forEach(f => {
          f.refinanced = false;
          f.rate = f.originalRate;
        });
        calculateMetrics();
        time = 0;
      }
      if (p.key === '+' || p.key === '=') speed = Math.min(speed + 0.5, 3);
      if (p.key === '-' || p.key === '_') speed = Math.max(speed - 0.5, 0.5);
      if (p.key >= '1' && p.key <= '4') {
        selectedFacility = parseInt(p.key) - 1;
      }
      if (p.key === 'a' || p.key === 'A') {
        // Refinance all
        facilities.forEach((f, i) => {
          if (!f.refinanced) toggleRefinance(i);
        });
      }
    };
    
    p.draw = () => {
      p.background(250);
      
      // Update simulation
      if (!paused && p.frameCount % Math.floor(30 / speed) === 0) {
        time++;
      }
      
      // Draw visualization
      drawTitle();
      drawDebtFacilities();
      drawInterestComparison();
      drawMaturityProfile();
      drawMetrics();
      drawControls();
    };
    
    const toggleRefinance = (index: number) => {
      const facility = facilities[index];
      facility.refinanced = !facility.refinanced;
      facility.rate = facility.refinanced ? facility.newRate : facility.originalRate;
      calculateMetrics();
    };
    
    const calculateMetrics = () => {
      totalInterest = 0;
      totalSavings = 0;
      let totalRefinancingCost = 0;
      
      facilities.forEach(f => {
        const annualInterest = f.principal * (f.rate / 100);
        totalInterest += annualInterest;
        
        if (f.refinanced) {
          const oldInterest = f.principal * (f.originalRate / 100);
          const annualSaving = oldInterest - annualInterest;
          totalSavings += annualSaving;
          totalRefinancingCost += f.refinancingCost;
        }
      });
      
      // NPV calculation (simplified)
      const npvSavings = totalSavings * 7.5; // Approximate 10-year NPV
      totalSavings = npvSavings - totalRefinancingCost;
    };
    
    const drawTitle = () => {
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(14);
      p.text('TitanForge Manufacturing - Debt Restructuring Optimization', 20, 15);
      
      p.textSize(11);
      p.fill(100);
      p.text('$85M Total Debt | Credit Rating: BBB+ | MILP Optimization', 20, 35);
      
      p.textSize(10);
      p.text('Click facility to select | Click button to refinance | A: Refinance all', 20, 55);
    };
    
    const drawDebtFacilities = () => {
      const barX = 50;
      const barY = 100;
      const barW = 350;
      const barH = 40;
      const spacing = 50;
      
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      
      for (let i = 0; i < facilities.length; i++) {
        const facility = facilities[i];
        const y = barY + i * spacing;
        const isSelected = i === selectedFacility;
        
        // Background
        p.fill(isSelected ? 240 : 255);
        p.stroke(isSelected ? 100 : 200);
        p.strokeWeight(isSelected ? 2 : 1);
        p.rect(barX, y, barW, barH);
        
        // Facility name and principal
        p.fill(40);
        p.noStroke();
        p.text(facility.name, barX + 5, y + 5);
        p.textSize(9);
        p.fill(100);
        p.text(`$${(facility.principal / 1000000).toFixed(0)}M`, barX + 5, y + 22);
        
        // Interest rate bar
        const maxRate = 7;
        const rateW = (facility.rate / maxRate) * (barW - 150);
        
        p.fill(facility.refinanced ? p.color(100, 200, 100) : facility.color);
        p.noStroke();
        p.rect(barX + 120, y + 8, rateW, 24);
        
        // Rate text
        p.fill(40);
        p.textSize(10);
        p.textAlign(p.CENTER, p.CENTER);
        p.text(`${facility.rate.toFixed(2)}%`, barX + 120 + rateW / 2, y + 20);
        
        // Refinance button
        const btnX = 410;
        const btnY = y + 5;
        const btnW = 100;
        const btnH = 30;
        
        p.fill(facility.refinanced ? p.color(100, 200, 100) : p.color(255, 200, 100));
        p.stroke(100);
        p.strokeWeight(1);
        p.rect(btnX, btnY, btnW, btnH, 5);
        
        p.fill(40);
        p.noStroke();
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(10);
        p.text(facility.refinanced ? '✓ Refinanced' : 'Refinance', btnX + btnW / 2, btnY + btnH / 2);
        
        // Savings indicator
        if (facility.refinanced) {
          const saving = facility.principal * (facility.originalRate - facility.newRate) / 100;
          p.fill(100, 180, 100);
          p.textAlign(p.LEFT, p.CENTER);
          p.textSize(9);
          p.text(`Saves $${(saving / 1000).toFixed(0)}K/yr`, btnX + btnW + 10, btnY + btnH / 2);
        }
      }
    };
    
    const drawInterestComparison = () => {
      const chartX = 550;
      const chartY = 100;
      const chartW = 230;
      const chartH = 180;
      
      // Background
      p.fill(255);
      p.stroke(200);
      p.rect(chartX, chartY, chartW, chartH);
      
      // Title
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      p.text('Annual Interest Cost', chartX + 10, chartY + 10);
      
      // Calculate before/after
      const beforeInterest = facilities.reduce((sum, f) => 
        sum + f.principal * (f.originalRate / 100), 0
      );
      const afterInterest = totalInterest;
      
      // Bars
      const barY = chartY + 40;
      const barH = 50;
      const maxInterest = 5000000;
      
      // Before bar
      const beforeW = (beforeInterest / maxInterest) * (chartW - 40);
      p.fill(255, 150, 150);
      p.noStroke();
      p.rect(chartX + 20, barY, beforeW, barH);
      
      p.fill(40);
      p.textAlign(p.LEFT, p.CENTER);
      p.textSize(9);
      p.text('Before', chartX + 25, barY - 10);
      p.text(`$${(beforeInterest / 1000000).toFixed(2)}M`, chartX + 25, barY + barH / 2);
      
      // After bar
      const afterW = (afterInterest / maxInterest) * (chartW - 40);
      p.fill(150, 255, 150);
      p.rect(chartX + 20, barY + barH + 20, afterW, barH);
      
      p.text('After', chartX + 25, barY + barH + 10);
      p.text(`$${(afterInterest / 1000000).toFixed(2)}M`, chartX + 25, barY + barH + 20 + barH / 2);
      
      // Savings
      const savings = beforeInterest - afterInterest;
      if (savings > 0) {
        p.fill(100, 180, 100);
        p.textAlign(p.CENTER, p.TOP);
        p.textSize(10);
        p.text(`💰 Saves $${(savings / 1000000).toFixed(2)}M/year`, chartX + chartW / 2, barY + barH * 2 + 35);
      }
    };
    
    const drawMaturityProfile = () => {
      const chartX = 550;
      const chartY = 300;
      const chartW = 230;
      const chartH = 100;
      
      // Background
      p.fill(255);
      p.stroke(200);
      p.rect(chartX, chartY, chartW, chartH);
      
      // Title
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      p.text('Maturity Profile', chartX + 10, chartY + 10);
      
      // Group by maturity year
      const maturityBuckets: {[key: number]: number} = {};
      facilities.forEach(f => {
        if (!maturityBuckets[f.maturity]) maturityBuckets[f.maturity] = 0;
        maturityBuckets[f.maturity] += f.principal;
      });
      
      // Draw bars
      const years = Object.keys(maturityBuckets).map(Number).sort((a, b) => a - b);
      const barW = (chartW - 40) / years.length;
      const maxPrincipal = 40000000;
      
      years.forEach((year, i) => {
        const amount = maturityBuckets[year];
        const barH = (amount / maxPrincipal) * 60;
        const x = chartX + 20 + i * barW;
        const y = chartY + chartH - 20 - barH;
        
        p.fill(100, 150, 255);
        p.noStroke();
        p.rect(x, y, barW - 5, barH);
        
        // Label
        p.fill(40);
        p.textAlign(p.CENTER, p.TOP);
        p.textSize(8);
        p.text(`Yr ${year}`, x + (barW - 5) / 2, chartY + chartH - 15);
        p.text(`$${(amount / 1000000).toFixed(0)}M`, x + (barW - 5) / 2, y - 12);
      });
    };
    
    const drawMetrics = () => {
      const metricsY = 420;
      
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      
      const totalDebt = facilities.reduce((sum, f) => sum + f.principal, 0);
      const weightedRate = (totalInterest / totalDebt) * 100;
      const dscr = ebitda / (totalInterest + totalDebt * 0.05); // Simplified DSCR
      
      p.text(`Total Debt: $${(totalDebt / 1000000).toFixed(0)}M`, 20, metricsY);
      p.text(`Weighted Avg Rate: ${weightedRate.toFixed(2)}%`, 20, metricsY + 18);
      p.text(`Annual Interest: $${(totalInterest / 1000000).toFixed(2)}M`, 20, metricsY + 36);
      p.text(`DSCR: ${dscr.toFixed(2)}x`, 20, metricsY + 54);
      
      // NPV and recommendation
      if (totalSavings > 0) {
        p.fill(100, 180, 100);
        p.text(`NPV of Savings: $${(totalSavings / 1000000).toFixed(1)}M`, 300, metricsY);
        p.text(`✓ Refinancing recommended`, 300, metricsY + 18);
      } else {
        p.fill(100);
        p.text(`Click facilities to evaluate refinancing options`, 300, metricsY);
      }
      
      // Target metrics
      p.fill(100);
      p.textSize(9);
      p.text('Target: <4.7% rate, >1.5x DSCR', 300, metricsY + 40);
    };
    
    const drawControls = () => {
      p.fill(100);
      p.noStroke();
      p.textAlign(p.LEFT, p.BOTTOM);
      p.textSize(9);
      
      p.text('Click: Select/Refinance | A: Refinance All | Space: Pause | R: Reset | +/-: Speed | 1-4: Select', 20, 490);
      
      p.textAlign(p.RIGHT, p.BOTTOM);
      p.fill(paused ? 200 : 100);
      p.text(paused ? 'PAUSED' : `RUNNING (${speed.toFixed(1)}x)`, 780, 490);
    };
  };

  return (
    <BaseSimulation
      sketch={sketch}
      title="Debt Restructuring Simulation"
      description="Interactive visualization of TitanForge Manufacturing's debt optimization using Mixed-Integer Linear Programming (MILP). Shows refinancing decisions, interest rate improvements, and NPV calculations."
    />
  );
};

export default DebtSimulation;
